import fp from "lodash/fp"
import { DateTime, Duration } from "luxon"

import type { Dispatch } from "@reduxjs/toolkit"
import type { SetURLSearchParams } from "react-router-dom"
import type { DateTimeValidationError } from "@mui/x-date-pickers"

import type {
  DateRange,
  DateRangeDateTime,
  DateRangeParams,
  DateReference,
} from "types/date.types"
import type { MultipleDateRangeAction } from "helpers/reducers/dateRangeReducer"
import type { EntityType } from "types/common.types"

import { formatISOToDateTime, hasForecast } from "helpers/utils/dateUtils"

interface DispatchCombo {
  setSearchParams: SetURLSearchParams
  dispatchDateRange: (dateRange: DateRange) => void
}

export const getRangeFromReference = (
  reference: DateReference,
  now: DateTime = DateTime.now(),
): { fromDate: DateTime; toDate: DateTime } => ({
  fromDate: now.minus({ [reference.timeUnit]: reference.timeAmount }),
  toDate:
    reference.forecastTimeAmount && reference.forecastTimeUnit
      ? now.plus({ [reference.forecastTimeUnit]: reference.forecastTimeAmount })
      : now,
})

export const formatDateRange = (dateRange: DateRange | null): DateRangeDateTime | null =>
  dateRange
    ? {
        range: dateRange.range,
        fromDate: DateTime.fromISO(dateRange.fromDate),
        toDate: DateTime.fromISO(dateRange.toDate),
        zoom: dateRange.zoom,
        forecastThresholdDate: DateTime.fromISO(dateRange.forecastThresholdDate),
      }
    : null

export const getCustomDateRangeFromParams = (
  dateRangeParams: DateRangeParams,
): DateRangeDateTime | null => {
  // The dateRangeParams have the values from the user input
  const { range, fromDate, toDate } = dateRangeParams
  const isCustom = range === "custom" && fromDate && toDate
  if (!isCustom) {
    return null
  }
  const dateNow = DateTime.now()
  const dateRange = {
    fromDate: DateTime.fromISO(fromDate),
    toDate: DateTime.fromISO(toDate),
    range: "custom",
    zoom: false, // custom always has zoom false
    forecastThresholdDate: dateNow,
  }
  return dateRange
}

export const getRangeDateRangeFromParams = (
  range: string | null,
  dateReferences: { [key: string]: DateReference },
  memoryRange: string | undefined,
  zoom?: string | null,
  fromDate?: string | null,
  toDate?: string | null,
): DateRangeDateTime | null => {
  const isNotRange = !range || range === "custom"
  const isAlreadyInMemory =
    memoryRange && memoryRange !== "custom" && memoryRange === range
  if (isNotRange || isAlreadyInMemory) return null

  const isZoomMode = zoom === "true"
  const dateNow = DateTime.now()

  // If in zoom mode with fromDate and toDate in params, use those values
  if (isZoomMode && fromDate && toDate) {
    const dateRange = {
      fromDate: DateTime.fromISO(fromDate),
      toDate: DateTime.fromISO(toDate),
      range,
      zoom: true,
      forecastThresholdDate: dateNow,
    }
    return dateRange
  }

  // Otherwise, calculate from dateReference
  const dateReferenceFromParams = dateReferences[range]
  const rangeFrom =
    dateReferenceFromParams &&
    dateNow.minus({
      [dateReferenceFromParams.timeUnit]: dateReferenceFromParams.timeAmount,
    })
  const rangeTo =
    dateReferenceFromParams?.forecastTimeAmount &&
    dateReferenceFromParams?.forecastTimeUnit
      ? dateNow.plus({
          [dateReferenceFromParams.forecastTimeUnit]:
            dateReferenceFromParams.forecastTimeAmount,
        })
      : dateNow

  const usefulRange = rangeFrom && rangeTo
  if (!usefulRange) {
    return null
  }
  const dateRange = {
    fromDate: rangeFrom,
    toDate: rangeTo,
    range,
    zoom: isZoomMode,
    forecastThresholdDate: dateNow,
  }
  return dateRange
}

const getDefaultDateRange = (entity: EntityType): DateRangeDateTime => {
  const dateNow = DateTime.now()
  if (hasForecast(entity)) {
    return {
      fromDate: dateNow.minus({ days: 1 }),
      toDate: dateNow.plus({ hours: 6 }),
      range: "lastDayPlusSixHours",
      zoom: false,
      forecastThresholdDate: dateNow,
    }
  }
  return {
    fromDate: dateNow.minus({ days: 7 }),
    toDate: dateNow,
    range: "lastWeek",
    zoom: false,
    forecastThresholdDate: dateNow,
  }
}

export const getDateRangeOrDefault = (
  dateRange: DateRange | null,
  entity: EntityType,
): DateRangeDateTime => {
  if (!dateRange) {
    return getDefaultDateRange(entity)
  }

  return {
    fromDate: formatISOToDateTime(dateRange.fromDate),
    toDate: formatISOToDateTime(dateRange.toDate),
    range: dateRange.range,
    zoom: dateRange.zoom,
    forecastThresholdDate: formatISOToDateTime(dateRange.forecastThresholdDate),
  }
}

export const setInitialDateRange = (
  initialDateRange: DateRangeDateTime,
  dispatchCombo: DispatchCombo,
): void => {
  const { range, fromDate, toDate, zoom, forecastThresholdDate } = initialDateRange
  const dateRange = {
    range,
    fromDate: String(fromDate.toISO()),
    toDate: String(toDate.toISO()),
    zoom,
    forecastThresholdDate: String(forecastThresholdDate.toISO()),
  }

  const updater = genUpdater(dispatchCombo)
  updater(dateRange)
}

export const setRangeDateRange = (
  dateReferenceName: string,
  dateReferences: { [key: string]: DateReference },
  dispatchCombo: DispatchCombo,
): void => {
  const updater = genUpdater(dispatchCombo)
  const dateNow = DateTime.now()

  const dateReference = dateReferences[dateReferenceName]
  const { fromDate, toDate } = getRangeFromReference(dateReference, dateNow)
  const dateRange = {
    fromDate: String(fromDate.toISO()),
    toDate: String(toDate.toISO()),
    range: dateReference.name,
    zoom: false,
    forecastThresholdDate: String(dateNow.toISO()),
  }

  updater(dateRange)
}

export const setCustomDateRange = (
  fields: { [key: string]: DateTime | null },
  currentDateRange: DateRangeDateTime,
  dispatchCombo: DispatchCombo,
): void => {
  const dateNow = DateTime.now()

  const dateRange = {
    range: "custom",
    fromDate: String(currentDateRange.fromDate.toISO()),
    toDate: String(currentDateRange.toDate.toISO()),
    zoom: false, // custom always has zoom false
    forecastThresholdDate: String(dateNow.toISO()),
  }

  const fieldsToISO = fp.mapValues((value: DateTime) => value.toISO(), fields)
  const updatedDateRange = fp.merge(dateRange, fieldsToISO)

  const updater = genUpdater(dispatchCombo)
  updater(updatedDateRange)
}

type GenUpdater = (combo: DispatchCombo) => (dateRange: DateRange) => void
const genUpdater: GenUpdater =
  ({ setSearchParams, dispatchDateRange }) =>
  (dateRange) => {
    const { range, zoom } = dateRange
    const isCustom = range === "custom"
    // For custom ranges, include full dateRange
    // For zoom mode (non-custom), include range, fromDate, toDate, and zoom
    // For normal ranges, only include range
    const searchParams: Record<string, string> = isCustom
      ? { range: dateRange.range, fromDate: dateRange.fromDate, toDate: dateRange.toDate }
      : zoom
        ? { range, fromDate: dateRange.fromDate, toDate: dateRange.toDate, zoom: "true" }
        : { range }

    setSearchParams(
      (prev: URLSearchParams) => {
        const pairs: [string, string][] = []
        prev.forEach((value: string, key: string) => {
          const isRangeFromDate = !isCustom && !zoom && key === "fromDate"
          const isRangeToDate = !isCustom && !zoom && key === "toDate"
          const isZoom = !zoom && key === "zoom"
          if (isRangeFromDate || isRangeToDate || isZoom) return
          return pairs.push([key, value])
        })
        return { ...fp.fromPairs(pairs), ...searchParams }
      },
      { replace: true },
    )
    dispatchDateRange(dateRange)
  }

export const initializePicker = (
  searchParams: URLSearchParams,
  dateRange: DateRange | null,
  dateReferences: { [key: string]: DateReference },
  entity: EntityType,
): { initialDateRange: DateRangeDateTime; defaultDateReference: DateReference } => {
  const range = searchParams.get("range")
  const fromDate = searchParams.get("fromDate")
  const toDate = searchParams.get("toDate")
  const zoom = searchParams.get("zoom")
  const dateRangeParams = { range, fromDate, toDate, zoom }

  const customDateRangeParams = getCustomDateRangeFromParams(dateRangeParams)
  const rangeDateRangeParams = getRangeDateRangeFromParams(
    dateRangeParams.range,
    dateReferences,
    dateRange?.range,
    dateRangeParams.zoom,
    dateRangeParams.fromDate,
    dateRangeParams.toDate,
  )
  const memoryOrDefaultDateRange = getDateRangeOrDefault(dateRange, entity)
  const initialDateRange = customDateRangeParams
    ? customDateRangeParams
    : rangeDateRangeParams
      ? rangeDateRangeParams
      : memoryOrDefaultDateRange

  const defaultDateReference = dateReferences[initialDateRange.range]

  return {
    initialDateRange,
    defaultDateReference,
  }
}

export const setAccordionDateRange = (
  alertInfo: { id: number; triggeredAt: string | undefined },
  dateReferenceName: string,
  dateReferences: { [key: string]: DateReference },
  dispatchMultipleDateRange: Dispatch<MultipleDateRangeAction>,
): void => {
  const { id, triggeredAt } = alertInfo
  // When triggeredAt value is missing in a triggered alert
  // the triggerWindow option will show the last week
  // triggeredAt should not be missing
  const dateReference =
    dateReferenceName === "triggerWindow" && !triggeredAt
      ? dateReferences.lastWeek
      : dateReferences[dateReferenceName]
  const dateNow = DateTime.now()

  const triggeredRefDate =
    triggeredAt &&
    dateReferenceName === "triggerWindow" &&
    (formatISOToDateTime(triggeredAt).minus({ days: 1 }).toISO() as string)
  const fromDate = triggeredRefDate
    ? triggeredRefDate
    : (dateNow
        .minus({
          [dateReference.timeUnit]: dateReference.timeAmount,
        })
        .toISO() as string)
  const toDate = dateNow.toISO() as string

  dispatchMultipleDateRange({
    type: "UPDATE",
    payload: {
      [id]: {
        range: dateReferenceName,
        fromDate,
        toDate,
        zoom: false,
        forecastThresholdDate: String(dateNow.toISO()),
      },
    },
  })
}

export const exitZoomMode = (
  currentDateRange: DateRangeDateTime,
  dateReferences: { [key: string]: DateReference },
  dispatchCombo: DispatchCombo,
): void => {
  const { range } = currentDateRange
  // If custom, just set zoom to false without recalculating
  if (range === "custom") {
    const updater = genUpdater(dispatchCombo)
    updater({
      range,
      fromDate: String(currentDateRange.fromDate.toISO()),
      toDate: String(currentDateRange.toDate.toISO()),
      zoom: false,
      forecastThresholdDate: DateTime.now().toISO() as string,
    })
    return
  }

  // For other ranges, recalculate with now
  const dateReference = dateReferences[range]
  if (!dateReference) {
    return
  }

  const dateNow = DateTime.now()
  const { fromDate, toDate } = getRangeFromReference(dateReference, dateNow)

  const updater = genUpdater(dispatchCombo)
  updater({
    fromDate: fromDate.toISO() as string,
    toDate: toDate.toISO() as string,
    range,
    zoom: false,
    forecastThresholdDate: dateNow.toISO() as string,
  })
}

export const setZoomDateRange = (
  start: DateTime,
  end: DateTime,
  currentDateRange: DateRange | null,
  dispatchCombo: DispatchCombo,
): void => {
  // Keep the current range, but set zoom to true only if not custom
  const currentRange = currentDateRange?.range || "lastWeek"
  const isCustomRange = currentRange === "custom"

  const updatedDateRange = {
    range: currentRange,
    fromDate: String(start.toISO()),
    toDate: String(end.toISO()),
    zoom: !isCustomRange, // Only set zoom true for non-custom ranges
    forecastThresholdDate: DateTime.now().toISO() as string,
  }

  const updater = genUpdater(dispatchCombo)
  updater(updatedDateRange)
}

export const getDateErrorMessage = (error: DateTimeValidationError) => {
  switch (error) {
    case "maxDate":
    case "maxTime":
      return "dates.MAX_DATE"
    case "disableFuture":
      return "dates.MAX_FUTURE"
    case "minDate":
    case "minTime":
      return "dates.MIN_DATE"
    case "invalidDate":
    default: {
      return "dates.INVALID_DATE"
    }
  }
}

/**
 * A range as short as it reads: "27 Oct 11:55 – 14:55", both days when it spans more than
 * one, and the years when it is not all in this one.
 */
export const formatRangeLabel = (
  fromDate: DateTime,
  toDate: DateTime,
  locale: string,
): string => {
  const from = fromDate.setLocale(locale)
  const to = toDate.setLocale(locale)
  const withYear = !from.hasSame(DateTime.now(), "year") || !to.hasSame(from, "year")
  const dayFormat = withYear ? "d LLL yyyy HH:mm" : "d LLL HH:mm"
  const toFormat = to.hasSame(from, "day") ? "HH:mm" : dayFormat
  return `${from.toFormat(dayFormat)} – ${to.toFormat(toFormat)}`
}

/** "1 day, 6 hours": the length of a range down to the minute, zero units left out. */
export const formatRangeDuration = (
  fromDate: DateTime,
  toDate: DateTime,
  locale: string,
): string => {
  const { years, months, days, hours, minutes } = toDate
    .diff(fromDate, ["years", "months", "days", "hours", "minutes"])
    .toObject()
  const amounts = { years, months, days, hours, minutes: Math.floor(minutes ?? 0) }
  const units = Object.fromEntries(
    Object.entries(amounts).filter(([, amount]) => (amount ?? 0) > 0),
  )
  return Duration.fromObject(units, { locale }).toHuman()
}

/**
 * "3 days": how much of a range that started in the past is still ahead of now, to the
 * nearest hour, since now never lands on a round minute. Under an hour it counts minutes
 * instead, and it is empty when less than a minute is forecast.
 */
export const formatForecastDuration = (
  toDate: DateTime,
  locale: string,
  now: DateTime = DateTime.now(),
): string => {
  const ahead = toDate.diff(now, "hours").hours
  if (ahead <= 0) return ""
  if (ahead < 1) return formatRangeDuration(now, toDate, locale)
  const hours = Math.round(ahead)
  return formatRangeDuration(toDate.minus({ hours }), toDate, locale)
}

/** Tall enough for the date range editor with its calendar open. */
const PANEL_HEIGHT = 526
/** What the panel leaves of the window when the window is short. */
const PANEL_WINDOW_GAP = 200
/** MUI's margin to the window edge, plus the paper's border. */
const PANEL_EDGE_SPACE = 16 + 2

/**
 * Below the trigger when the panel fits there, otherwise on the side with more room, and
 * never taller than that room, so MUI does not move the paper over the trigger.
 */
export const getPanelPlacement = (
  anchorEl: HTMLElement | null,
  panelHeight = PANEL_HEIGHT,
): { above: boolean; height: number } => {
  const fullHeight = Math.min(panelHeight, window.innerHeight - PANEL_WINDOW_GAP)
  if (!anchorEl) return { above: false, height: fullHeight }
  const { top, bottom } = anchorEl.getBoundingClientRect()
  const spaceBelow = window.innerHeight - bottom - PANEL_EDGE_SPACE
  const spaceAbove = top - PANEL_EDGE_SPACE
  const above = spaceBelow < fullHeight && spaceAbove > spaceBelow
  return { above, height: Math.min(fullHeight, above ? spaceAbove : spaceBelow) }
}
